import React, { FC, useState } from "react";
import { IntlProvider } from "react-intl";

import I18nContext from "./I18nContext";
import i18n from "../services/i18n";

interface I18nProviderProps {
  children: React.ReactNode;
}

/** Messages already logged, so each one is reported once per page load. */
const reported = new Set<string>();

export const I18nProvider: FC<I18nProviderProps> = ({ children }) => {
  const [locale, setLocale] = useState(i18n.locale);

  const changeLocale = (newLocale: string) => {
    setLocale(newLocale);
  };

  const handleError = (error: Error) => {
    if (error.message.includes(".routes.")) {
      return;
    }

    // Once per message. `onError` fires on every render that formats an
    // untranslated key, so a component that re-renders on a timer (an animated
    // placeholder, a countdown) otherwise floods the console until the tab
    // freezes. The first log carries all the information the later ones do.
    if (reported.has(error.message)) {
      return;
    }

    reported.add(error.message);

    if (process.env.NODE_ENV === "development") {
      console.warn(error);
    } else {
      console.error(error);
    }
  };

  return (
    <I18nContext.Provider value={{ locale, changeLocale }}>
      <IntlProvider
        locale={locale}
        messages={i18n.messages}
        onError={handleError}
      >
        {children}
      </IntlProvider>
    </I18nContext.Provider>
  );
};
